<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h4>تتبّع دخول الطلاب للامتحانات ({{ events.total ?? 0 }})</h4>
        <button class="btn btn-danger text-white" type="button" :disabled="clearing" @click="confirmClearAll">
          مسح كل السجل
        </button>
      </div>

      <div class="row mb-3">
        <div class="col-lg-4 col-md-6 col-12">
          <div class="form-group">
            <label for="word">ابحث عن طالب</label>
            <input type="search" id="word" class="form-control" placeholder="اسم الطالب / رقم الطالب / الهاتف"
                   v-model="word" @keyup="debounceFetch" />
          </div>
        </div>
        <div class="col-lg-4 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>المرحلة التعليمية</label>
            <Dropdown v-model="academicYearId" :options="yearOptions" showClear optionLabel="name" optionValue="id"
                      placeholder="كل المراحل" @change="fetchEvents(1)" />
          </div>
        </div>
        <div class="col-lg-4 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>نوع الحدث</label>
            <Dropdown v-model="eventType" :options="eventTypeOptions" showClear optionLabel="title" optionValue="id"
                      placeholder="كل الأحداث" @change="fetchEvents(1)" />
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group">
            <label for="examId">رقم الامتحان</label>
            <input type="number" id="examId" class="form-control" placeholder="مثال: 14"
                   v-model="examId" @keyup="debounceFetch" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group">
            <label for="sessionId">جلسة التسجيل</label>
            <input type="number" id="sessionId" class="form-control" placeholder="رقم الجلسة"
                   v-model="registrationSessionId" @keyup="debounceFetch" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group">
            <label for="from">من</label>
            <input type="datetime-local" id="from" class="form-control" v-model="from" @change="fetchEvents(1)" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group">
            <label for="to">إلى</label>
            <input type="datetime-local" id="to" class="form-control" v-model="to" @change="fetchEvents(1)" />
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>التاريخ والوقت (الخادم)</th>
            <th>الطالب</th>
            <th>المرحلة</th>
            <th>الحدث</th>
            <th>الامتحان</th>
            <th>الحالة / النتيجة</th>
            <th>جلسة التسجيل</th>
            <th>الجهاز</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in events.data" :key="e.id">
            <td class="text-nowrap">{{ formatDate(e.visited_at) }}</td>
            <td>
              {{ e.student_name ?? '-' }}
              <small class="text-muted d-block">#{{ e.user_id }}</small>
            </td>
            <td>{{ e.academic_year_name ?? '-' }}</td>
            <td>
              <span class="status-badge" :class="`status-badge--${eventColor(e.event_type)}`">
                <i class="pi" :class="eventIcon(e.event_type)"></i>
                {{ eventLabel(e.event_type) }}
              </span>
            </td>
            <td>{{ examCell(e) }}</td>
            <td>{{ resultCell(e) }}</td>
            <td>{{ e.registration_session_id ? ('#' + e.registration_session_id) : 'الأصلي' }}</td>
            <td>{{ e.device_id ? ('#' + e.device_id) : (e.device_uuid ? shortUuid(e.device_uuid) : '-') }}</td>
            <td>{{ e.ip_address ?? '-' }}</td>
          </tr>
          <tr v-if="!events.data || events.data.length === 0">
            <td colspan="9" class="text-center py-4">لا يوجد سجل بعد</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row" v-if="events.total">
      <div class="col-12">
        <pagination :data="events" class="mx-auto d-flex align-items-center justify-content-center pagination"
                    @pagination-change-page="fetchEvents">
          <template v-slot:prev-nav>&lt;</template>
          <template v-slot:next-nav>&gt;</template>
        </pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dropdown from 'primevue/dropdown';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

const EVENT_LABELS = {
  exams_page_view:   'فتح صفحة الامتحانات',
  exam_open:         'فتح امتحان',
  exams_api_request: 'طلب قائمة الامتحانات',
};
const EVENT_ICONS = {
  exams_page_view:   'pi-eye',
  exam_open:         'pi-file-edit',
  exams_api_request: 'pi-server',
};
// Reuses the global status-badge palette (design-system.css).
const EVENT_COLORS = {
  exams_page_view:   'active',
  exam_open:         'accepted',
  exams_api_request: 'waiting',
};

export default {
  name: 'ExamAccessReport',
  components: { Breadcrumb, Dropdown, pagination },
  data() {
    return {
      events: { data: [], total: 0 },
      word: '',
      academicYearId: null,
      eventType: null,
      examId: '',
      registrationSessionId: '',
      from: '',
      to: '',
      yearOptions: [],
      eventTypeOptions: Object.entries(EVENT_LABELS).map(([id, title]) => ({ id, title })),
      searchTimeout: null,
      clearing: false,
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'تتبّع دخول الامتحانات', path: '/students/exam-access' },
      ],
    };
  },
  methods: {
    eventLabel(t) { return EVENT_LABELS[t] ?? t; },
    eventIcon(t) { return EVENT_ICONS[t] ?? 'pi-circle'; },
    eventColor(t) { return EVENT_COLORS[t] ?? 'blocked'; },
    shortUuid(u) { return u && u.length > 8 ? u.slice(0, 8) + '…' : u; },
    formatDate(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ar-EG');
    },
    examCell(e) {
      if (e.event_type === 'exam_open') {
        return e.exam_title ? `${e.exam_title} (#${e.exam_id})` : `#${e.exam_id}`;
      }
      return '-';
    },
    // "✓ Exam #14 was returned" style summary for API rows; result/status otherwise.
    resultCell(e) {
      if (e.event_type === 'exams_api_request') {
        const parts = [`HTTP ${e.http_status ?? '-'}`, `أرجع ${e.returned_count ?? 0} امتحان`];
        if (e.filter_exam_present === true) parts.push(`✓ الامتحان ${this.examId} ظهر له`);
        else if (e.filter_exam_present === false) parts.push(`✗ الامتحان ${this.examId} لم يظهر له`);
        return parts.join(' — ');
      }
      return e.http_status ? `HTTP ${e.http_status}` : '✓';
    },
    debounceFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchEvents(1), 600);
    },
    fetchEvents(page = 1) {
      const params = { page };
      if (this.word) params.word = this.word;
      if (this.academicYearId) params.academic_year_id = this.academicYearId;
      if (this.eventType) params.event_type = this.eventType;
      if (this.examId) params.exam_id = this.examId;
      if (this.registrationSessionId) params.registration_session_id = this.registrationSessionId;
      if (this.from) params.from = this.from.replace('T', ' ');
      if (this.to) params.to = this.to.replace('T', ' ');

      axios.get('/admin/exam-access/events', { params }).then(({ data }) => {
        this.events = data.data;
      }).catch(() => {
        this.events = { data: [], total: 0 };
      });
    },
    fetchYears() {
      // Active-only source (same endpoint the devices/audit-logs filter and the grade
      // store use) — never show disabled/inactive education levels in this filter.
      axios.get('/academic-years/active').then(({ data }) => {
        this.yearOptions = data.data || [];
      }).catch(() => { this.yearOptions = []; });
    },
    confirmClearAll() {
      Swal.fire({
        title: 'مسح كل السجل؟',
        html: 'سيتم حذف <b>كل</b> سجلات تتبّع دخول الطلاب للامتحانات نهائياً ولا يمكن التراجع عن ذلك.<br>لن يتأثر الطلاب أو الحجوزات أو الامتحانات أو النتائج.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'متابعة',
        cancelButtonText: 'إلغاء',
      }).then((result) => {
        if (!result.isConfirmed) return;
        // Second, stronger confirmation for this irreversible bulk action.
        Swal.fire({
          title: 'تأكيد نهائي',
          text: 'هل أنت متأكد تماماً؟ سيتم حذف كل السجلات بشكل نهائي.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'نعم، امسح كل السجل',
          cancelButtonText: 'رجوع',
          confirmButtonColor: '#dc3545',
        }).then((confirmResult) => {
          if (!confirmResult.isConfirmed) return;
          this.clearAll();
        });
      });
    },
    clearAll() {
      this.clearing = true;
      axios.delete('/admin/exam-access/events').then(({ data }) => {
        Swal.fire('', `تم مسح ${data.deleted ?? 0} سجل بنجاح`, 'success');
        this.fetchEvents(1);
      }).catch((err) => {
        Swal.fire('', err.response?.data?.message || 'تعذر مسح السجل', 'error');
      }).finally(() => { this.clearing = false; });
    },
  },
  mounted() {
    this.fetchYears();
    this.fetchEvents();
  },
};
</script>
